import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL query for fetching all roles with permissions
export const GET_ALL_ROLES_QUERY = gql`
  query FindAllRoles {
    findAllRoles {
      id
      name
      permissions {
        id
        description
        name
      }
    }
  }
`;

// GraphQL mutation for creating a role
export const CREATE_ROLE_MUTATION = gql`
  mutation CreateRole($data: CreateRoleInput!) {
    createRole(data: $data) {
      id
      name
      permissions {
        id
        name
        description
      }
    }
  }
`;

// GraphQL mutation for assigning permission to role
export const ASSIGN_PERMISSION_TO_ROLE_MUTATION = gql`
  mutation AssignPermissionToRole($roleId: Int!, $permissionId: Int!) {
    assignPermissionToRole(roleId: $roleId, permissionId: $permissionId) {
      id
      name
      permissions {
        id
        name
        description
      }
    }
  }
`;

// GraphQL mutation for removing permission from role
export const REMOVE_PERMISSION_FROM_ROLE_MUTATION = gql`
  mutation RemovePermissionFromRole($roleId: Int!, $permissionId: Int!) {
    removePermissionFromRole(roleId: $roleId, permissionId: $permissionId) {
      id
      name
      permissions {
        id
        name
        description
      }
    }
  }
`;

// Types for the response
export interface Permission {
  id: number;
  name: string;
  description: string;
}

export interface Role {
  id: number;
  name: string;
  permissions: Permission[];
}

export interface GetAllRolesResponse {
  findAllRoles: Role[];
}

export interface CreateRoleInput {
  name: string;
  permissionIds?: number[];
}

export interface CreateRoleResponse {
  createRole: Role;
}

export interface AssignPermissionResponse {
  assignPermissionToRole: Role;
}

export interface RemovePermissionResponse {
  removePermissionFromRole: Role;
}

// Roles service class
export class RolesService {
  private static instance: RolesService;

  private constructor() {}

  public static getInstance(): RolesService {
    if (!RolesService.instance) {
      RolesService.instance = new RolesService();
    }
    return RolesService.instance;
  }

  // Fetch all roles with their permissions
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await apolloClient.query<GetAllRolesResponse>({
        query: GET_ALL_ROLES_QUERY,
        fetchPolicy: 'network-only',
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.findAllRoles) {
        throw new Error('No data returned from getAllRoles query');
      }
      return response.data.findAllRoles;
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Create a new role
  async createRole(roleData: CreateRoleInput): Promise<Role> {
    try {
      const response = await apolloClient.mutate<CreateRoleResponse>({
        mutation: CREATE_ROLE_MUTATION,
        variables: { data: roleData },
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.createRole) {
        throw new Error('No data returned from create role mutation');
      }
      return response.data.createRole;
    } catch (error: any) {
      console.error('Error creating role:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Assign permission to role
  async assignPermissionToRole(
    roleId: number,
    permissionId: number,
  ): Promise<Role> {
    try {
      const response = await apolloClient.mutate<AssignPermissionResponse>({
        mutation: ASSIGN_PERMISSION_TO_ROLE_MUTATION,
        variables: { roleId, permissionId },
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.assignPermissionToRole) {
        throw new Error('No data returned from assign permission mutation');
      }
      return response.data.assignPermissionToRole;
    } catch (error: any) {
      console.error('Error assigning permission to role:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Remove permission from role
  async removePermissionFromRole(
    roleId: number,
    permissionId: number,
  ): Promise<Role> {
    try {
      const response = await apolloClient.mutate<RemovePermissionResponse>({
        mutation: REMOVE_PERMISSION_FROM_ROLE_MUTATION,
        variables: { roleId, permissionId },
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.removePermissionFromRole) {
        throw new Error('No data returned from remove permission mutation');
      }
      return response.data.removePermissionFromRole;
    } catch (error: any) {
      console.error('Error removing permission from role:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const rolesService = RolesService.getInstance();

// Export convenience functions
export async function getAllRoles(): Promise<Role[]> {
  return rolesService.getAllRoles();
}

export async function createRole(roleData: CreateRoleInput): Promise<Role> {
  return rolesService.createRole(roleData);
}

export async function assignPermissionToRole(
  roleId: number,
  permissionId: number,
): Promise<Role> {
  return rolesService.assignPermissionToRole(roleId, permissionId);
}

export async function removePermissionFromRole(
  roleId: number,
  permissionId: number,
): Promise<Role> {
  return rolesService.removePermissionFromRole(roleId, permissionId);
}
